import { Button, Input, Select, Switch, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  ABAC_OPS,
  ABAC_PATHS,
  type AbacConditionModel,
  type AbacGroup,
  type AbacOp,
  type AbacPredicate,
  emptyPredicate,
} from './abacCondition';

const { Text } = Typography;

/**
 * The registry-aware ABAC condition builder (OPSFL-FSD-IAM-UC2 §6) — the Screen Studio pattern:
 * a visual, closed-vocabulary surface over the SAME artifact the Advanced JSON tab edits. The
 * path select offers ONLY the 15 registry paths, the op select ONLY the five ops — an attribute
 * or operator outside the closed world cannot be picked here at all (the JSON tab can still type
 * one; the pre-flight check and the server refuse it). Value editors follow the op: one literal
 * for eq/ne, a literal list for in/nin, a boolean for exists.
 */

const PATH_OPTIONS = ABAC_PATHS.map((p) => ({ value: p.path }));

const PATH_BLURB = new Map(ABAC_PATHS.map((p) => [p.path, p.blurb]));

const OP_OPTIONS = ABAC_OPS.map((op) => ({ value: op }));

const COMBINE_OPTIONS = [
  { value: 'all', label: 'ALL match (and)' },
  { value: 'any', label: 'ANY matches (or)' },
];

function coerceValue(op: AbacOp, prev: AbacPredicate['value']): AbacPredicate['value'] {
  if (op === 'exists') return typeof prev === 'boolean' ? prev : true;
  if (op === 'in' || op === 'nin') {
    if (Array.isArray(prev)) return prev;
    return typeof prev === 'string' && prev !== '' ? [prev] : [];
  }
  if (typeof prev === 'string') return prev;
  return Array.isArray(prev) ? (prev[0] ?? '') : '';
}

function PredicateRow({
  predicate,
  onChange,
  onRemove,
  removable,
}: {
  predicate: AbacPredicate;
  onChange: (next: AbacPredicate) => void;
  onRemove: () => void;
  removable: boolean;
}) {
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <Select
        aria-label="Attribute path"
        size="small"
        style={{ width: 210 }}
        value={predicate.path}
        options={PATH_OPTIONS}
        showSearch
        optionFilterProp="value"
        virtual={false} /* 15 fixed options — keep the whole closed list in the DOM */
        onChange={(path) => onChange({ ...predicate, path })}
      />
      <Select
        aria-label="Operator"
        size="small"
        style={{ width: 90 }}
        value={predicate.op}
        options={OP_OPTIONS}
        virtual={false} /* five fixed ops — keep the whole closed list in the DOM */
        onChange={(op: AbacOp) => onChange({ ...predicate, op, value: coerceValue(op, predicate.value) })}
      />
      {predicate.op === 'exists' ? (
        <Switch
          aria-label="Exists value"
          size="small"
          checked={predicate.value === true}
          checkedChildren="present"
          unCheckedChildren="absent"
          onChange={(checked) => onChange({ ...predicate, value: checked })}
        />
      ) : predicate.op === 'in' || predicate.op === 'nin' ? (
        <Select
          aria-label="Literal list"
          size="small"
          mode="tags"
          style={{ minWidth: 220, flex: 1 }}
          placeholder="literal values (enter to add)"
          value={Array.isArray(predicate.value) ? predicate.value : []}
          tokenSeparators={[',']}
          open={false}
          suffixIcon={null}
          onChange={(value: string[]) => onChange({ ...predicate, value })}
        />
      ) : (
        <Input
          aria-label="Literal value"
          size="small"
          style={{ minWidth: 180, flex: 1 }}
          placeholder="one literal value"
          value={typeof predicate.value === 'string' ? predicate.value : ''}
          onChange={(e) => onChange({ ...predicate, value: e.target.value })}
        />
      )}
      {removable && (
        <Button
          size="small"
          type="text"
          icon={<DeleteOutlined />}
          aria-label="Remove predicate"
          onClick={onRemove}
        />
      )}
      </div>
      <Text type="secondary" style={{ fontSize: 11.5 }}>
        {PATH_BLURB.get(predicate.path)}
      </Text>
    </div>
  );
}

export function AbacConditionBuilder({
  model,
  onChange,
}: {
  model: AbacConditionModel;
  onChange: (next: AbacConditionModel) => void;
}) {
  const setItem = (index: number, next: AbacPredicate | AbacGroup) => {
    onChange({ ...model, items: model.items.map((it, i) => (i === index ? next : it)) });
  };
  const removeItem = (index: number) => {
    onChange({ ...model, items: model.items.filter((_, i) => i !== index) });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Select
          aria-label="Combinator"
          size="small"
          style={{ width: 170 }}
          value={model.combine}
          options={COMBINE_OPTIONS}
          onChange={(combine) => onChange({ ...model, combine })}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          of the rules below — the policy DENIES when the condition matches.
        </Text>
      </div>

      {model.items.map((item, i) =>
        item.kind === 'predicate' ? (
          <PredicateRow
            key={i}
            predicate={item}
            removable={model.items.length > 1}
            onChange={(next) => setItem(i, next)}
            onRemove={() => removeItem(i)}
          />
        ) : (
          <div
            key={i}
            style={{
              border: '1px solid var(--ol-border, #d9d9d9)',
              borderRadius: 6,
              padding: '8px 10px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Select
                aria-label="Group combinator"
                size="small"
                style={{ width: 170 }}
                value={item.combine}
                options={COMBINE_OPTIONS}
                onChange={(combine) => setItem(i, { ...item, combine })}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                nested group (the grammar allows one level — depth 3 total)
              </Text>
              <Button
                size="small"
                type="text"
                icon={<DeleteOutlined />}
                aria-label="Remove group"
                onClick={() => removeItem(i)}
              />
            </div>
            {item.predicates.map((p, j) => (
              <PredicateRow
                key={j}
                predicate={p}
                removable={item.predicates.length > 1}
                onChange={(next) =>
                  setItem(i, { ...item, predicates: item.predicates.map((q, k) => (k === j ? next : q)) })}
                onRemove={() =>
                  setItem(i, { ...item, predicates: item.predicates.filter((_, k) => k !== j) })}
              />
            ))}
            <Button
              size="small"
              icon={<PlusOutlined />}
              style={{ alignSelf: 'flex-start' }}
              onClick={() => setItem(i, { ...item, predicates: [...item.predicates, emptyPredicate()] })}
            >
              Rule in group
            </Button>
          </div>
        ),
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={() => onChange({ ...model, items: [...model.items, emptyPredicate()] })}
        >
          Add rule
        </Button>
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={() =>
            onChange({
              ...model,
              items: [...model.items, { kind: 'group', combine: 'any', predicates: [emptyPredicate()] }],
            })}
        >
          Add group
        </Button>
      </div>
    </div>
  );
}
